import { Condition, CreditRating, FinanceType } from './constants';
import type { CreditRange, TradeIn } from './dealTypes';
import type {
  DealerFees,
  InceptionFeesConfig,
  TaxInCapCostConfig,
} from './dealerTypes';
import type { Bundle, SelectedProduct } from './penTypes';
import type { SelectedAccessory } from './accessoryTypes';
import type { AddOn, ProtectionPlanProductPriceOverrides } from './pencilTypes';
import type { CashOffer, LeaseOffer, PurchaseOffer } from './paymentTypes';
import { PropertyChange } from './propertyChangeTypes';

/* BaseOfferData common fields across all Purchase/Lease/Cash Offers */
export type BaseOfferData = {
  // NOTE: Ideally I would like to mark the mandatory fields in baseoffer
  // but at this point, I am not sure which fields are mandatory or not.
  deskingOfferId?: string;
  msrp?: number | null;
  condition?: Condition;
  downPayment?: number;
  downPaymentOptions?: number[];
  creditRange?: CreditRange;
  creditRating?: CreditRating;
  pencilTradeIns?: TradeIn[] | null;
  tradeInAllowance?: number;
  tradeInTaxCredit?: boolean;
  tradePayoff?: number;
  tradeACV?: number;
  invoice?: number;
  bookValue?: number;
  cost?: number;
  fees?: DealerFees | null;
  freight?: number;
  pack?: number;
  addOns?: AddOn[];
  daysToFirstPayment?: number;
  show10DollarMonthlyPriceRange?: boolean;
  addInceptionFeesToTotalAtStart?: boolean;
  inceptionFeesTotalAtStart?: InceptionFeesConfig;
  includeTaxInCapCost?: boolean;
  taxInCapCost?: TaxInCapCostConfig;
  selectedBundles?: Bundle[];
  selectedCustomerTypeId?: number;
  selectedProducts?: SelectedProduct[];
  selectedAccessories?: SelectedAccessory[] | null;
  protectionPlanProductPriceOverrides?: ProtectionPlanProductPriceOverrides;
  capitalizedTaxOverride?: number;
  /**
   * Maximum Residualized MSRP
   */
  mrm?: number;

  // REL-16735 - Indicates whether default selected products should be calculated.
  // This is to be removed once we move default product selection for desking offers to the backend upon creation.
  ignoreDefaultProductSelection?: boolean;
};

/* PurchaseOfferData with financeType field */
export type PurchaseOfferData = BaseOfferData &
  PurchaseOffer & {
    financeType: FinanceType.PURCHASE;
  };

/* LeaseOfferData with financeType field */
export type LeaseOfferData = BaseOfferData &
  LeaseOffer & {
    financeType: FinanceType.LEASE;
  };

/* CashOfferData with financeType field */
export type CashOfferData = BaseOfferData &
  CashOffer & {
    financeType: FinanceType.CASH;
  };

/* Union type for OfferData, use the financeType discriminator */
export type OfferData = PurchaseOfferData | LeaseOfferData | CashOfferData;

export enum GroupName {
  TERMS = 'terms',
  INCENTIVES = 'incentives',
  TAXABLE_FEES = 'taxableFees',
  NON_TAXABLE_FEES = 'nonTaxableFees',
  CAPITALIZED_COST_FEES = 'capitalizedCostFees',
  CAPITALIZED_COST_TAXABLE_FEES = 'capitalizedCostTaxableFees',
  ADD_ONS = 'addOns',
  PROTECTION_PLANS = 'protectionPlans',
  PROTECTION_PLAN_OVERRIDES = 'protectionPlanOverrides',
  ACCESSORIES = 'accessories',
  TRADE_INS = 'tradeIns',
}

export type ChangeLog = {
  [index in GroupName]?: {
    [key: string]: {
      old?: any;
      new: any;
      changeMadeBy?: string;
    };
  };
};

// The new Desking types below

export interface Desking {
  id: string;
  dealId?: string | null;
  carId?: string | null;
  acceptedOfferId?: string | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
}

export interface DeskingOffer {
  id: string;
  deskingId?: string | null;
  isSelectedOffer?: boolean;
  data?: OfferData | null;
  changeLog?: ChangeLog | null;
  pushedToCRMAt?: Date | null;
  sentToSalespersonAt?: Date | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
}

export interface DeskingVersion {
  id: string;
  deskingId: string;
  version: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface DeskingOfferHistory {
  id: string;
  deskingOfferId: string;
  data?: OfferData | null;
  condition?: Condition | null;
  parentId?: string | null;
  isDefaultOffer?: boolean | null;
  restored?: boolean | null;
  deleted?: boolean | null;
  clonedFrom?: string | null;
  deskingVersionId: string;
  createdBy: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface DeskingOfferHistoryInfo extends DeskingOfferHistory {
  version: number;
  propertyChanges?: PropertyChange[];
  createdByName: string;
}
